import { useEffect, useState } from "react";

import { useCart, useRoofingEstimator } from "../../../hooks/query";
import type { LaborRowItem } from "../types";
import {
    buildLaborEditsFromRows,
    mapLaborLineItemsResponse,
} from "../utils/laborLineItems";
import { hydrateLaborFromCart } from "../utils/cartMapping";

import { useEstimateContractContextManager } from "./";

export default function useLaborInit(projectId?: string) {
    const { setValues, setReferenceValue } = useEstimateContractContextManager();
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [laborRows, setLaborRows] = useState<LaborRowItem[]>([]);

    const { useLaborLineItems } = useRoofingEstimator();
    const laborLineItemsSvc = useLaborLineItems();
    const { useListByProjectId } = useCart();
    const listCartSvc = useListByProjectId();

    const loadLaborData = async (id: string, isCancelled?: () => boolean) => {
        setLoading(true);
        setError(null);
        try {
            const data = await laborLineItemsSvc.mutateAsync({
                ProjectId: id,
            });
            if (isCancelled?.()) return { success: false };

            const rows = mapLaborLineItemsResponse(data);
            setLaborRows(rows);

            const baseEdits = buildLaborEditsFromRows(rows);
            let labor = baseEdits;
            let cartIds: Record<string, string> = {};

            if (Object.keys(baseEdits).length > 0) {
                const cartItems = await listCartSvc.mutateAsync({
                    ProjectId: id,
                });
                if (isCancelled?.()) return { success: false };

                const hydrated = hydrateLaborFromCart(
                    Array.isArray(cartItems) ? cartItems : [],
                    rows,
                    baseEdits,
                );
                labor = hydrated.labor;
                cartIds = hydrated.cartIds;
            }

            if (isCancelled?.()) return { success: false };

            const applyLoadedLabor = (prev: any) => ({
                ...prev,
                labor,
            });

            setValues(applyLoadedLabor);
            setReferenceValue((prev: any) => ({
                ...applyLoadedLabor(prev),
                cartIds: {
                    ...(prev.cartIds || {}),
                    ...cartIds,
                },
            }));

            return { success: true };
        } catch (err: any) {
            if (!isCancelled?.()) {
                setError(err.message || "Failed to load labor");
            }
            return { success: false, error: err };
        } finally {
            if (!isCancelled?.()) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        if (!projectId) return;

        let cancelled = false;
        loadLaborData(projectId, () => cancelled);

        return () => {
            cancelled = true;
        };
    }, [projectId]);

    return {
        loadLaborData,
        laborRows,
        loading,
        error,
    };
}
